import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import {
    selectIsDeliveryPartnerMode,
    selectPartnerInfo,
    selectDeliveryPartnerUserInfo,
    selectSessionId,
    restoreFromSession,
    validateSession,
    clearPartnerInfo,
    updateCustomNotes
} from '../store/reducer/deliveryPartnerSlice';
import { fetchCartInfo } from '../store/thunk/cartThunk';
import axiosInstance from '../api/axiosInstance';
import { selectUser } from '../store/reducer/authSlice';
import { selectCartData, clearLocalCart } from '../store/reducer/cartSlice';

/**
 * Secure hook for accessing delivery partner information
 * Automatically validates session and restores from encrypted storage
 */
export const useDeliveryPartner = () => {
    const dispatch = useDispatch();

    // Redux selectors
    const isDeliveryPartnerMode = useSelector(selectIsDeliveryPartnerMode);
    const partnerInfo = useSelector(selectPartnerInfo);
    const userInfo = useSelector(selectDeliveryPartnerUserInfo);
    const sessionId = useSelector(selectSessionId);
    const user = useSelector(selectUser);
    const cartData = useSelector(selectCartData);

    // Auto-restore and validate session on hook initialization
    useEffect(() => {
        dispatch(restoreFromSession());
        dispatch(validateSession());
    }, [dispatch]);

    // Validate session periodically (every 5 minutes)
    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(validateSession());
        }, 300000); // 5 minutes

        return () => clearInterval(interval);
    }, [dispatch]);

    // Helper functions
    const clearPartnerData = async (options = {}) => {
        const { removeAllCart, type = "delivery-partner" } = options;

        // If user is logged in and removeAllCart is true, call clear cart API
        if (user && removeAllCart) {
            try {
                const { data } = await axiosInstance.delete('/cart/clear-cart', {
                    data: {
                        order_type: type
                    }
                });

                // Check if API call was successful
                if (data.status === 1) {
                    // Refresh cart data after successful removal
                    await dispatch(fetchCartInfo());
                } else {
                    console.error('Clear cart API failed:', data.message);
                }
            } catch (error) {
                console.error('Failed to clear cart:', error);
            }
        }

        // Clear partner data from Redux store
        dispatch(clearPartnerInfo());
    };

    const saveCustomNotes = (notes) => {
        dispatch(updateCustomNotes(notes));
    };

    const isValidSession = () => {
        return sessionId && isDeliveryPartnerMode && partnerInfo;
    };

    const getPartnerDetails = () => {
        if (!isValidSession()) return null;

        return {
            shortName: partnerInfo?.shortName,
            partnerId: partnerInfo?.partnerId,
            name: partnerInfo?.name,
            address: partnerInfo?.address,
        };
    };

    const getUserDetails = () => {
        // Return userInfo even if session validation fails (for custom notes)
        if (!userInfo) return null;

        return {
            userId: userInfo?.userId,
            userRole: userInfo?.userRole,
            phone: userInfo?.phone,
            custom_notes: userInfo?.custom_notes,
        };
    };

    return {
        // State
        isDeliveryPartnerMode: isValidSession(),
        partnerInfo: getPartnerDetails(),
        userInfo: getUserDetails(),
        sessionId,

        // Actions
        clearPartnerData,
        saveCustomNotes,

        // Helpers
        isValidSession,
        hasPartnerInfo: () => Boolean(partnerInfo),
        hasUserInfo: () => Boolean(userInfo),
    };
};

export default useDeliveryPartner;